import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { Plus, X, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIdentity } from "contexts/identity-context";
import { createClipConfig } from "data/mutations-common";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React, { Ref, useCallback, useEffect, useMemo, useState } from "react";
import { MapRef } from "react-map-gl";
import { v4 as uuidv4 } from "uuid";
import { CircleMode, SimpleSelectMode } from "./circle-lib";
import { ClipConfiguration, Status } from "./models";
import { NewClip } from "./steps/step-0-new-clip/new-clip";
import { DefineBoundary } from "./steps/step-1-define-boundary/define-boundary";
//@ts-ignore
import DrawRectangle from "mapbox-gl-draw-rectangle-mode";
//@ts-ignore
import StaticMode from "@mapbox/mapbox-gl-draw-static-mode";
import { usePermissions } from "components/permissions/usePermissions";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import * as QueryKeys from "data";
import { fetchClipsPaginated } from "data/queries-common";
import { useLocation } from "react-router-dom";
import { resolveIdentityId } from "utils/identity";
import * as Routes from "../../../../routes";
import { zoomToFeatureCollection } from "../../explorer/utils";
import { drawStyles } from "./draw-styles";
import { ChooseData } from "./steps/step-2-choose-data/choose-data";
import { FinaliseClip } from "./steps/step-3-finalise/finalise-clip";
import { ClipSummary } from "./steps/step-4-summary/clip-summary";
import { PREMIUM_PRODUCT_KEYS } from "./catalogue/catalogue";
const stepsMapping = [
  {
    stepName: "New Clip",
    element: NewClip,
  },
  {
    stepName: "Define the clip boundary",
    element: DefineBoundary,
  },
  {
    stepName: "Choose Data",
    element: ChooseData,
  },
  {
    stepName: "Finalise Clip",
    element: FinaliseClip,
  },
  {
    stepName: "Clip Summary",
    element: ClipSummary,
  },
];

export const hasPremiumData = (clip: ClipConfiguration) => {
  const premiumAttrs =
    clip.attributes?.filter(
      (x) => PREMIUM_PRODUCT_KEYS.includes(x.key) && x.attributes.length > 0,
    ) ?? [];
  return premiumAttrs.length > 0;
};

export interface ClipStepsControlProps {
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  maxSteps: number;
}

export interface ClipConfigProps {
  clipConfig: ClipConfiguration;
  setClipConfig: React.Dispatch<React.SetStateAction<ClipConfiguration>>;
  saveClipConfig: (cfg?: ClipConfiguration) => void;
  resetClipConfig: () => void;
  mapRef: Ref<MapRef>;
  draw: MapboxDraw;
  clips: ClipConfiguration[];
  setExpanded: (expanded: boolean) => void;
}

interface ClipMenuProps {
  setClipOpen: (b: boolean) => void;
  mapRef: Ref<MapRef>;
  initialClipId?: string;
}

export const ClipMenu = (props: ClipMenuProps) => {
  const { isPermissionsLoading } = usePermissions();
  const location = useLocation();
  const [identityState] = useIdentity();
  const queryClient = useQueryClient();

  // Initialize MapboxDraw once
  const draw = useMemo(
    () =>
      new MapboxDraw({
        boxSelect: false,
        keybindings: false,
        touchEnabled: false,
        displayControlsDefault: false,
        modes: {
          ...MapboxDraw.modes,
          draw_circle: CircleMode,
          simple_select: SimpleSelectMode,
          draw_rectangle: DrawRectangle,
          static: StaticMode,
        },
        styles: drawStyles,
      }),
    [],
  );

  dayjs.extend(utc);
  const createNewClipConfig = useCallback(
    (): ClipConfiguration => ({
      id: uuidv4(),
      name: "",
      activeStep: 0,
      status: "Draft",
      date: dayjs.utc().format(),
    }),
    [],
  );

  const identityId = resolveIdentityId(identityState);

  const { enqueueQueryFailed } = useCustomSnackbars();

  const clipQueryKey = [QueryKeys.clipsKey, identityId, "firstPage"];

  const clipsQuery = useQuery({
    queryKey: clipQueryKey,
    queryFn: async () => {
      // Fetch only the first page with limit of 50
      return await fetchClipsPaginated(0, 50);
    },
    refetchInterval: (query) => {
      // Check if any clips are in Quoting or Processing status
      const hasActiveClips = query.state.data?.clips.some(
        (clip: ClipConfiguration) =>
          clip.status === "Quoting" || clip.status === "Processing",
      );
      return hasActiveClips ? 5000 : 30000;
    },
    // Refetch in background to keep data updated even when tab is not focused
    refetchIntervalInBackground: true,
  });

  // Get clips directly from the response
  const allClips = useMemo(() => {
    return clipsQuery.data?.clips ?? [];
  }, [clipsQuery.data]);

  // Track if we've loaded the initial clip from URL
  const [hasLoadedInitialClip, setHasLoadedInitialClip] = useState(false);
  const isWaitingForInitialClip =
    !!props.initialClipId && !clipsQuery.isSuccess;

  // Initialize clip config once we have data (or immediately if no initial clip needed)
  const [clipConfig, setClipConfig] = useState<ClipConfiguration>(() => {
    if (props.initialClipId && clipsQuery.isSuccess) {
      const foundClip = allClips.find(
        (clip) => clip.id === props.initialClipId,
      );
      if (foundClip) {
        setHasLoadedInitialClip(true);
      }
      return foundClip ?? createNewClipConfig();
    }
    return createNewClipConfig();
  });

  // Expanded state - derive from route or explicit toggle
  const shouldBeExpandedFromRoute = location.pathname === Routes.clipUi;
  const [manuallyExpanded, setManuallyExpanded] = useState(
    !!props.initialClipId,
  );
  const expanded = shouldBeExpandedFromRoute || manuallyExpanded;

  // Save mutation with optimistic updates
  const clipsCreateMutation = useMutation<
    ClipConfiguration,
    Error,
    ClipConfiguration,
    { previousClips: unknown }
  >({
    mutationFn: (clipConfig: ClipConfiguration) => createClipConfig(clipConfig),
    // Optimistic update: runs IMMEDIATELY when mutation starts
    onMutate: async (savedClip: ClipConfiguration) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: clipQueryKey });

      // Snapshot the previous value for rollback
      const previousClips = queryClient.getQueryData(clipQueryKey);

      // Optimistically update the cache
      queryClient.setQueryData<{ clips: ClipConfiguration[] }>(
        clipQueryKey,
        (old) => {
          if (!old?.clips) return old;

          // Add current timestamp to mimic backend behavior
          const updatedClip: ClipConfiguration = {
            ...savedClip,
            lastEditedAt: dayjs.utc().valueOf(), // Unix timestamp in milliseconds
          };

          const existingIndex = old.clips.findIndex(
            (c) => c.id === savedClip.id,
          );

          let updatedClips: ClipConfiguration[];
          if (existingIndex >= 0) {
            // Update existing clip
            updatedClips = [...old.clips];
            updatedClips[existingIndex] = updatedClip;
          } else {
            // Add new clip
            updatedClips = [updatedClip, ...old.clips];
          }

          // Sort by lastEditedAt descending (most recent first) to mimic backend ordering
          updatedClips.sort((a, b) => {
            const timestampA = a.lastEditedAt ?? dayjs(a.date).valueOf();
            const timestampB = b.lastEditedAt ?? dayjs(b.date).valueOf();
            return timestampB - timestampA;
          });

          return { ...old, clips: updatedClips };
        },
      );

      // Return context with snapshot for rollback
      return { previousClips };
    },
    // If mutation fails, rollback to previous state
    onError: (_error: Error, _variables: ClipConfiguration, context) => {
      if (context?.previousClips) {
        queryClient.setQueryData(clipQueryKey, context.previousClips);
      }
    },
    // Always refetch after error or success to get server truth
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: clipQueryKey });
    },
  });

  // Memoized save function
  const saveClipConfig = useCallback(
    (config?: ClipConfiguration) => {
      const cfg = config ?? clipConfig;

      // Don't save if clip is in a read-only state
      const readOnlyStatuses: Status[] = ["Quoting", "Processing", "Completed"];
      if (readOnlyStatuses.includes(cfg.status)) {
        return;
      }

      if (cfg.name && cfg.activeStep > 0) {
        clipsCreateMutation.mutate(cfg);
      }
    },
    [clipConfig, clipsCreateMutation],
  );

  // Memoized reset function
  const resetClipConfigForm = useCallback(() => {
    draw.deleteAll();
    setClipConfig(createNewClipConfig());
  }, [draw, createNewClipConfig]);

  // Memoized close handler
  const handleClose = useCallback(() => {
    saveClipConfig();
    resetClipConfigForm();
    if (clipConfig.activeStep === 0) {
      setManuallyExpanded(false);
      props.setClipOpen(false);
    }
  }, [saveClipConfig, resetClipConfigForm, clipConfig.activeStep, props]);

  // Single setter for activeStep that updates clipConfig directly
  const setActiveStep = useCallback(
    (step: number | ((prev: number) => number)) => {
      setClipConfig((prev) => {
        const newStep =
          typeof step === "function" ? step(prev.activeStep) : step;

        // Update draw mode based on step
        if (!draw) return { ...prev, activeStep: newStep };

        if (newStep > 1) {
          draw.changeMode(MapboxDraw.constants.modes.STATIC);
        } else if (newStep === 1) {
          draw.changeMode(MapboxDraw.constants.modes.SIMPLE_SELECT);
        }

        const newConfig = { ...prev, activeStep: newStep };

        // Auto-save when changing steps (unless loading initial clip)
        if (!isWaitingForInitialClip) {
          // Use setTimeout to avoid state update during render
          setTimeout(() => saveClipConfig(newConfig), 0);
        }

        return newConfig;
      });
    },
    [draw, isWaitingForInitialClip, saveClipConfig],
  );

  // Effect 1: Load initial clip from URL (runs once when data is ready)
  useEffect(() => {
    if (props.initialClipId && clipsQuery.isSuccess && !hasLoadedInitialClip) {
      const initialClip = allClips.find(
        (clip) => clip.id === props.initialClipId,
      );
      if (initialClip) {
        setClipConfig(initialClip);
        setHasLoadedInitialClip(true);
      }
    }
  }, [
    props.initialClipId,
    clipsQuery.isSuccess,
    hasLoadedInitialClip,
    allClips,
  ]);

  // Effect 2: Show query errors
  useEffect(() => {
    if (clipsQuery.isError) {
      enqueueQueryFailed(clipsQuery.error.toString());
    }
  }, [clipsQuery.isError, clipsQuery.error, enqueueQueryFailed]);

  // Effect 3: Add draw control to map (runs once when map is ready)
  useEffect(() => {
    if (!props.mapRef) return;
    //@ts-ignore
    const mapInstance = props.mapRef.current;
    if (!mapInstance) return;
    //@ts-ignore
    if (!mapInstance.hasControl(draw)) {
      //@ts-ignore
      mapInstance.addControl(draw);
    }
  }, [props.mapRef, draw]);

  // Effect 4: Load geometry onto map when clip changes
  useEffect(() => {
    if (!draw || !clipConfig.geometry) return;

    // Clear existing features
    draw.deleteAll();

    // Add new features
    clipConfig.geometry.features.forEach((f) => {
      draw.add(f);
    });

    // Zoom to features
    zoomToFeatureCollection(props.mapRef, clipConfig.geometry);
  }, [draw, clipConfig.id, clipConfig.geometry, props.mapRef]);

  return (
    <>
      {!expanded && (
        <Card className="w-29">
          <button
            className="w-full cursor-pointer"
            onClick={() => {
              setManuallyExpanded(true);
              props.setClipOpen(true);
            }}
            disabled={clipsCreateMutation.isPending || clipsQuery.isPending}
          >
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Clip</CardTitle>
              <Button
                variant="ghost"
                size="icon-sm"
                data-cy="open-clip-window"
                disabled={clipsCreateMutation.isPending || clipsQuery.isPending}
                tabIndex={-1}
                asChild
              >
                <span>
                  <Plus className="size-4" />
                </span>
              </Button>
            </CardHeader>
          </button>
        </Card>
      )}
      {expanded && (
        <>
          {(clipsQuery.isPending || isPermissionsLoading) && (
            <Card className="h-full w-full flex flex-col">
              <CardContent className="w-full flex-1 flex flex-col pb-1">
                <Skeleton className="w-full flex-1" />
              </CardContent>
            </Card>
          )}
          {clipsQuery.isSuccess && !isPermissionsLoading && (
            <Card className="h-full w-full flex flex-col">
              <CardHeader className="w-full shrink-0 flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Clip</CardTitle>
                  {clipConfig.name && (
                    <CardDescription className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">
                        {clipConfig.id}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        onClick={() =>
                          navigator.clipboard.writeText(clipConfig.id)
                        }
                      >
                        <Copy className="size-3 text-muted-foreground" />
                      </Button>
                    </CardDescription>
                  )}
                </div>
                <CardAction>
                  <Button variant="ghost" size="icon-sm" onClick={handleClose}>
                    <X className="size-4" />
                  </Button>
                </CardAction>
              </CardHeader>
              <CardContent className="w-full flex-1 overflow-hidden flex flex-col pb-1">
                {React.createElement(
                  stepsMapping[clipConfig.activeStep].element,
                  {
                    activeStep: clipConfig.activeStep,
                    setActiveStep: setActiveStep,
                    maxSteps: stepsMapping.length - 1,
                    clipConfig: clipConfig,
                    setClipConfig: setClipConfig,
                    saveClipConfig: saveClipConfig,
                    mapRef: props.mapRef,
                    draw: draw,
                    clips: allClips,
                    setExpanded: setManuallyExpanded,
                    resetClipConfig: resetClipConfigForm,
                  },
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </>
  );
};
